import { DatePicker, Divider, Grid, message, Select } from "antd";
import dayjs from "dayjs";
import React, { useEffect, useRef, useState } from "react";
import { useDebounce } from "use-debounce";

import arrowDown from "../../assets/icons/arrow-down12.png";
import { controller } from "./controller";

const {useBreakpoint} = Grid;

export default function TreatmentFilters({ setTreatmentFilters, treatmentFilters }) {
  // Shared procedure options state
  const [procedureDropdownOptions, setProcedureDropdownOptions] = useState([]);
  const [isGettingProcedures, setIsGettingProcedures] = useState(false);

  // Not-completed procedure search
  const [ncProcedureSearch, setNcProcedureSearch] = useState("");
  const [ncProcedureSearchValue] = useDebounce(ncProcedureSearch, 1000);
  const [isFilteringNcProcedures, setIsFilteringNcProcedures] = useState(false);
  const [ncProcedureDropdownOpen, setNcProcedureDropdownOpen] = useState(false);
  const skipInitialNcFetch = useRef(true);

  // Completed procedure search
  const [cProcedureSearch, setCProcedureSearch] = useState("");
  const [cProcedureSearchValue] = useDebounce(cProcedureSearch, 1000);
  const [isFilteringCProcedures, setIsFilteringCProcedures] = useState(false);
  const [cProcedureDropdownOpen, setCProcedureDropdownOpen] = useState(false);
  const skipInitialCFetch = useRef(true);

  // Planned procedure search
  const [plannedProcedureSearch, setPlannedProcedureSearch] = useState("");
  const [plannedProcedureSearchValue] = useDebounce(plannedProcedureSearch, 1000);
  const [isFilteringPlannedProcedures, setIsFilteringPlannedProcedures] = useState(false);
  const [plannedProcedureDropdownOpen, setPlannedProcedureDropdownOpen] = useState(false);
  const skipInitialPlannedFetch = useRef(true);

  const screens = useBreakpoint()
  const isBetweenLgXl = screens.lg && !screens.xl;
  const isLargeView = screens.lg;

  const monthsOptions = [
    { label: "Past 1 month", value: "1" },
    { label: "Past 3 months", value: "3" },
    { label: "Past 6 months", value: "6" },
    { label: "Past 12 months", value: "12" },
    { label: "Past 24 months", value: "24" },
    { label: "Past 36 months", value: "36" },
    { label: "Past 48 months", value: "48" },
  ];

  // Getting all procedures on mount
  useEffect(function () {
    async function getAllProcedures() {
      try {
        setIsGettingProcedures(true);
        const res = await controller.getProcedure(0, {});
        const procedureOptions = res.json.map((procedure) => ({
          value: procedure.id,
          label: `${procedure.procedure_code} ${procedure.procedure_code_description ? `- ${procedure.procedure_code_description}` : ""}`,
        }));
        setProcedureDropdownOptions(procedureOptions);
      } catch (err) {
        message.error("Something went wrong while fetching procedures");
        console.error(err);
      } finally {
        setIsGettingProcedures(false);
      }
    }
    getAllProcedures();
  }, []);

  // Debounced search for not-completed procedures
  useEffect(() => {
    const fetchProcedures = async () => {
      if (ncProcedureSearchValue === "" && skipInitialNcFetch.current) {
        skipInitialNcFetch.current = false;
        return;
      }
      skipInitialNcFetch.current = false;
      const query = typeof ncProcedureSearchValue === "string" ? ncProcedureSearchValue.trim() : "";
      try {
        setIsFilteringNcProcedures(true);
        const response = await controller.getProcedure(0, { query: query });
        if (response.status < 250 && Array.isArray(response.json)) {
          setProcedureDropdownOptions(response.json.map((p) => ({
            value: p.id,
            label: `${p.procedure_code} ${p.procedure_code_description ? `- ${p.procedure_code_description}` : ""}`,
          })));
        }
      } catch (err) {
        message.error("Something went wrong while filtering procedures");
        console.error(err);
      } finally {
        setIsFilteringNcProcedures(false);
      }
    };
    fetchProcedures();
  }, [ncProcedureSearchValue]);

  // Debounced search for completed procedures
  useEffect(() => {
    const fetchProcedures = async () => {
      if (cProcedureSearchValue === "" && skipInitialCFetch.current) {
        skipInitialCFetch.current = false;
        return;
      }
      skipInitialCFetch.current = false;
      const query = typeof cProcedureSearchValue === "string" ? cProcedureSearchValue.trim() : "";
      try {
        setIsFilteringCProcedures(true);
        const response = await controller.getProcedure(0, { query: query });
        if (response.status < 250 && Array.isArray(response.json)) {
          setProcedureDropdownOptions(response.json.map((p) => ({
            value: p.id,
            label: `${p.procedure_code} ${p.procedure_code_description ? `- ${p.procedure_code_description}` : ""}`,
          })));
        }
      } catch (err) {
        message.error("Something went wrong while filtering procedures");
        console.error(err);
      } finally {
        setIsFilteringCProcedures(false);
      }
    };
    fetchProcedures();
  }, [cProcedureSearchValue]);

  // Debounced search for planned procedures
  useEffect(() => {
    const fetchProcedures = async () => {
      if (plannedProcedureSearchValue === "" && skipInitialPlannedFetch.current) {
        skipInitialPlannedFetch.current = false;
        return;
      }
      skipInitialPlannedFetch.current = false;
      const query = typeof plannedProcedureSearchValue === "string" ? plannedProcedureSearchValue.trim() : "";
      try {
        setIsFilteringPlannedProcedures(true);
        const response = await controller.getProcedure(0, { query: query });
        if (response.status < 250 && Array.isArray(response.json)) {
          setProcedureDropdownOptions(response.json.map((p) => ({
            value: p.id,
            label: `${p.procedure_code} ${p.procedure_code_description ? `- ${p.procedure_code_description}` : ""}`,
          })));
        }
      } catch (err) {
        message.error("Something went wrong while filtering procedures");
        console.error(err);
      } finally {
        setIsFilteringPlannedProcedures(false);
      }
    };
    fetchProcedures();
  }, [plannedProcedureSearchValue]);

  var plannedProcedureOptions = [{ value: "all", label: "All Procedures" }].concat(procedureDropdownOptions);

  return (
    <div style={{ width: "100%" }} className="treatment-filters-container">
      {/* Row 1: Not Completed Within + Procedures */}
      <div className="treatment-filter-column">
        <SelectDropdown
          label="Not Completed Within"
          options={monthsOptions}
          placeholder="Select Time Period"
          value={treatmentFilters.not_completed_within_months || undefined}
          onChange={(value) => setTreatmentFilters((prevFilters) => ({ ...prevFilters, not_completed_within_months: value }))}
          onClear={() => setTreatmentFilters((prevFilters) => ({ ...prevFilters, not_completed_within_months: "", not_completed_procedure_ids: [] }))}
        />

        {isLargeView && <Divider type="vertical" style={{ height: 65, marginTop: 10, backgroundColor: "#B7B7B7", marginInline: isBetweenLgXl ? 0 : 30  }} />}

        <div className="treatment-from-to-date-inputs">
          <label className="treatment-filter-inputs-label">Procedures</label>
          <Select
            mode="multiple"
            showSearch
            filterOption={false}
            options={procedureDropdownOptions}
            placeholder="Select Procedures"
            disabled={!treatmentFilters.not_completed_within_months}
            value={treatmentFilters.not_completed_procedure_ids || undefined}
            onChange={(value) => setTreatmentFilters((prevFilters) => ({ ...prevFilters, not_completed_procedure_ids: value }))}
            onOpenChange={(open) => setNcProcedureDropdownOpen(open)}
            onClear={() => setTreatmentFilters((prevFilters) => ({ ...prevFilters, not_completed_procedure_ids: [] }))}
            onSearch={(q) => setNcProcedureSearch(q != null ? q : "")}
            loading={isFilteringNcProcedures || isGettingProcedures}
            style={{ width: "100%", height: 42 }}
            suffixIcon={
              <img style={{ width: 20, transform: `rotate(${ncProcedureDropdownOpen ? "180deg" : "0"})` }} src={arrowDown} alt="arrow-down" />
            }
            className="treatment-filter-input"
            allowClear={true}
            maxTagCount="responsive"
          />
        </div>
      </div>

      {/* Row 2: Completed Within + Procedures */}
      <div className="treatment-filter-column">
        <SelectDropdown
          label="Completed Within"
          options={monthsOptions}
          placeholder="Select Time Period"
          value={treatmentFilters.completed_within_months || undefined}
          onChange={(value) => setTreatmentFilters((prevFilters) => ({ ...prevFilters, completed_within_months: value }))}
          onClear={() => setTreatmentFilters((prevFilters) => ({ ...prevFilters, completed_within_months: "", completed_procedure_ids: [] }))}
        />

        {isLargeView && <Divider type="vertical" style={{ height: 65, marginTop: 10, backgroundColor: "#B7B7B7", marginInline: isBetweenLgXl ? 0 : 30  }} />}

        <div className="treatment-from-to-date-inputs">
          <label className="treatment-filter-inputs-label">Procedures</label>
          <Select
            mode="multiple"
            showSearch
            filterOption={false}
            options={procedureDropdownOptions}
            placeholder="Select Procedures"
            disabled={!treatmentFilters.completed_within_months}
            value={treatmentFilters.completed_procedure_ids || undefined}
            onChange={(value) => setTreatmentFilters((prevFilters) => ({ ...prevFilters, completed_procedure_ids: value }))}
            onOpenChange={(open) => setCProcedureDropdownOpen(open)}
            onClear={() => setTreatmentFilters((prevFilters) => ({ ...prevFilters, completed_procedure_ids: [] }))}
            onSearch={(q) => setCProcedureSearch(q != null ? q : "")}
            loading={isFilteringCProcedures || isGettingProcedures}
            style={{ width: "100%", height: 42 }}
            suffixIcon={
              <img style={{ width: 20, transform: `rotate(${cProcedureDropdownOpen ? "180deg" : "0"})` }} src={arrowDown} alt="arrow-down" />
            }
            className="treatment-filter-input"
            allowClear={true}
            maxTagCount="responsive"
          />
        </div>
      </div>

      {/* Row 4: Planned Within + Procedures */}
      <div className="treatment-filter-column">
        <SelectDropdown
          label="Planned Procedures Within"
          options={monthsOptions}
          placeholder="Select Time Period"
          value={treatmentFilters.planned_within_months || undefined}
          onChange={(value) => setTreatmentFilters((prevFilters) => ({ ...prevFilters, planned_within_months: value }))}
          onClear={() => setTreatmentFilters((prevFilters) => ({ ...prevFilters, planned_within_months: "", planned_procedure_ids: [] }))}
        />

        {isLargeView && <Divider type="vertical" style={{ height: 65, marginTop: 10, backgroundColor: "#B7B7B7", marginInline: isBetweenLgXl ? 0 : 30  }} />}

        <div className="treatment-from-to-date-inputs">
          <label className="treatment-filter-inputs-label">Procedures</label>
          <Select
            mode="multiple"
            showSearch
            filterOption={false}
            options={plannedProcedureOptions}
            placeholder="Select Procedures"
            disabled={!treatmentFilters.planned_within_months}
            value={treatmentFilters.planned_procedure_ids || undefined}
            onChange={(value) => {
              if (value.indexOf("all") !== -1 && (!treatmentFilters.planned_procedure_ids || treatmentFilters.planned_procedure_ids.indexOf("all") === -1)) {
                setTreatmentFilters((prevFilters) => ({ ...prevFilters, planned_procedure_ids: ["all"] }));
              } else if (value.indexOf("all") !== -1 && value.length > 1) {
                setTreatmentFilters((prevFilters) => ({ ...prevFilters, planned_procedure_ids: value.filter(function (v) { return v !== "all"; }) }));
              } else {
                setTreatmentFilters((prevFilters) => ({ ...prevFilters, planned_procedure_ids: value }));
              }
            }}
            onOpenChange={(open) => setPlannedProcedureDropdownOpen(open)}
            onClear={() => setTreatmentFilters((prevFilters) => ({ ...prevFilters, planned_procedure_ids: [] }))}
            onSearch={(q) => setPlannedProcedureSearch(q != null ? q : "")}
            loading={isFilteringPlannedProcedures || isGettingProcedures}
            style={{ width: "100%", height: 42 }}
            suffixIcon={
              <img style={{ width: 20, transform: `rotate(${plannedProcedureDropdownOpen ? "180deg" : "0"})` }} src={arrowDown} alt="arrow-down" />
            }
            className="treatment-filter-input"
            allowClear={true}
            maxTagCount="responsive"
          />
        </div>
      </div>

      {/* Row 5: Latest Breakdown After + Latest Estimate After.
          Uses the same 50/50 grid as the rows above (inputs capped at 664px,
          left-aligned) so these align under — and don't overrun — the fields
          above. */}
      <div className="treatment-filter-column">
        <div className="treatment-from-to-date-inputs">
          <label className="treatment-filter-inputs-label">Latest Breakdown After</label>
          <DatePicker
            value={treatmentFilters.latest_breakdown_after ? dayjs(treatmentFilters.latest_breakdown_after) : null}
            onChange={(d) =>
              setTreatmentFilters((prevFilters) => ({ ...prevFilters, latest_breakdown_after: d ? d.format("YYYY-MM-DD") : "" }))
            }
            placeholder="Select date"
            allowClear
            style={{ width: "100%", height: 42 }}
            className="treatment-filter-input"
          />
        </div>

        {isLargeView && <Divider type="vertical" style={{ height: 65, marginTop: 10, backgroundColor: "#B7B7B7", marginInline: isBetweenLgXl ? 0 : 30 }} />}

        <div className="treatment-from-to-date-inputs">
          <label className="treatment-filter-inputs-label">Latest Estimate After</label>
          <DatePicker
            value={treatmentFilters.latest_estimate_after ? dayjs(treatmentFilters.latest_estimate_after) : null}
            onChange={(d) =>
              setTreatmentFilters((prevFilters) => ({ ...prevFilters, latest_estimate_after: d ? d.format("YYYY-MM-DD") : "" }))
            }
            placeholder="Select date"
            allowClear
            style={{ width: "100%", height: 42 }}
            className="treatment-filter-input"
          />
        </div>
      </div>

      {/* Row 6: Appointment Date Range — left column only (empty right keeps the
          50/50 grid so it aligns under the left fields above). */}
      <div className="treatment-filter-column">
        <div className="treatment-from-to-date-inputs">
          <label className="treatment-filter-inputs-label">Appointment Date Range</label>
          <DatePicker.RangePicker
            value={
              treatmentFilters.appointment_start && treatmentFilters.appointment_end
                ? [dayjs(treatmentFilters.appointment_start), dayjs(treatmentFilters.appointment_end)]
                : null
            }
            onChange={(range) =>
              setTreatmentFilters((prevFilters) => ({
                ...prevFilters,
                appointment_start: range && range[0] ? range[0].format("YYYY-MM-DD") : "",
                appointment_end: range && range[1] ? range[1].format("YYYY-MM-DD") : "",
              }))
            }
            allowClear
            style={{ width: "100%", height: 42 }}
            className="treatment-filter-input"
          />
        </div>

        {isLargeView && <Divider type="vertical" style={{ height: 65, marginTop: 10, backgroundColor: "#B7B7B7", marginInline: isBetweenLgXl ? 0 : 30 }} />}

        <div className="treatment-from-to-date-inputs" />
      </div>
    </div>
  );
}

function SelectDropdown({ label, onChange, placeholder, options, onClear, ...extra }) {
  const [isArrowDown, setIsArrowDown] = useState(true);

  return (
    <div className="treatment-from-to-date-inputs">
      <label className="treatment-filter-inputs-label">{label}</label>

      <Select
        {...extra}
        onChange={onChange}
        options={options}
        placeholder={placeholder}
        style={{ width: "100%", height: 42 }}
        suffixIcon={<img style={{ width: 20, transform: `rotate(${isArrowDown ? "0" : "180deg"})` }} src={arrowDown} alt="arrow-down" />}
        className="treatment-filter-input"
        onOpenChange={() => setIsArrowDown((open) => !open)}
        allowClear={true}
        maxTagCount="responsive"
        onClear={onClear}
      />
    </div>
  );
}
